import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { useTranslation } from 'react-i18next'
import {
  createMapSnapshotMutation,
  deleteSnapshotMutation,
  getControlDataSchemasOptions,
  getControlDataSchemasQueryKey,
  getSnapshotListWithOverridesOptions,
  getSnapshotListWithOverridesQueryKey,
  syncMapsMutation,
} from '@/api/main/@tanstack/react-query.gen'
import type { SyncResponse } from '@/api/main/types.gen'
import { sortControlSchemas } from './sortControlSchemas'

const errorMessage = (error: unknown, fallback: string): string => {
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message
  }
  return fallback
}

export const useMapSnapshots = () => {
  const { t } = useTranslation('mapSnapshots')
  const queryClient = useQueryClient()

  const snapshotsQuery = useSuspenseQuery(getSnapshotListWithOverridesOptions())
  const schemasQuery = useSuspenseQuery(getControlDataSchemasOptions())

  const sortedSchemas = sortControlSchemas(schemasQuery.data)

  const invalidateSnapshots = () => queryClient.invalidateQueries({ queryKey: getSnapshotListWithOverridesQueryKey() })
  const invalidateSchemas = () => queryClient.invalidateQueries({ queryKey: getControlDataSchemasQueryKey() })

  const createSnapshot = useMutation({
    ...createMapSnapshotMutation(),
    onSuccess: (_data, variables) => {
      invalidateSnapshots()
      notifications.show({
        title: t('createSnapshot'),
        message: t('snapshotCreated', { name: variables.body.snapshot_name }),
        color: 'green',
      })
    },
    onError: error => {
      notifications.show({
        title: t('createSnapshot'),
        message: errorMessage(error, t('createFailed')),
        color: 'red',
      })
    },
  })

  const syncSnapshot = useMutation({
    ...syncMapsMutation(),
    onSuccess: (_data, variables) => {
      invalidateSchemas()
      notifications.show({
        title: t('syncSnapshot'),
        message: t('snapshotSynced', {
          snapshot: variables.body.snapshot_from,
          schema: variables.body.destination_schema,
        }),
        color: 'green',
      })
    },
    onError: error => {
      notifications.show({
        title: t('syncSnapshot'),
        message: errorMessage(error, t('syncFailed')),
        color: 'red',
      })
    },
  })

  const deleteSnapshot = useMutation({
    ...deleteSnapshotMutation(),
    onSuccess: (_data, variables) => {
      invalidateSnapshots()
      notifications.show({
        title: t('deleteSnapshot'),
        message: t('snapshotDeleted', { name: variables.body.snapshot_name }),
        color: 'green',
      })
    },
    onError: error => {
      notifications.show({
        title: t('deleteSnapshot'),
        message: errorMessage(error, t('deleteFailed')),
        color: 'red',
      })
    },
  })

  return {
    snapshots: snapshotsQuery.data,
    schemas: sortedSchemas,
    createSnapshot,
    syncSnapshot,
    deleteSnapshot,
  }
}

export type SyncResult = SyncResponse[]
